/**
 * CoupDePouceGuide
 * ─────────────────────────────────────────────────────────────────
 * Guide éditorial complet pour /particuliers/aides/coup-de-pouce.
 *
 * Structure :
 *  1. Hero (eyebrow + titre + lead + sidecard)
 *  2. Bandeau "En résumé"
 *  3. Mécanisme (bonification CEE, charte qualité)
 *  4. Les Coups de pouce en vigueur (3 dispositifs)
 *  5. Barèmes selon profils Anah
 *  6. Conditions
 *  7. Démarches (5 étapes)
 *  8. Cumul avec autres aides
 *  9. FAQ
 * 10. CTA final
 *
 * Contenu : `client/src/config/coup-de-pouce-content.ts`.
 */

import { ArrowRight, CheckCircle2, Info } from 'lucide-react';

import { Container } from '../layout/Container';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ArticleLd } from '../seo/jsonld/Article';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { HowToLd } from '../seo/jsonld/HowTo';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import {
  CTA_HREF,
  SectionHeader,
  SITE_URL,
} from './_shared';
import {
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierLiveCard,
  ParticulierPageHero,
} from '../audience/particulier';
import {
  COUP_BAREMES,
  COUP_CONDITIONS,
  COUP_CUMULS,
  COUP_DEMARCHES,
  COUP_FAQ,
  COUP_HERO,
  COUP_MECANISME,
  COUP_OPERATIONS,
  COUP_SUMMARY,
} from '../../config/coup-de-pouce-content';

const PAGE_URL = `${SITE_URL}/particuliers/aides/coup-de-pouce`;

const CUMUL_TONE_STYLES: Record<
  'brand' | 'accent' | 'sage' | 'lavender' | 'ivory',
  string
> = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  sage: 'bg-stone-100 text-stone-700 ring-stone-200/60',
  lavender: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  ivory: 'bg-ivory-100 text-stone-700 ring-stone-200/60',
};

const INCOME_COLOR_STYLES: Record<'bleu' | 'jaune' | 'violet' | 'rose', string> = {
  bleu: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200/70',
  jaune: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200/70',
  violet: 'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/70',
  rose: 'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200/70',
};

const INCOME_DOT_STYLES: Record<'bleu' | 'jaune' | 'violet' | 'rose', string> = {
  bleu: 'bg-sky-500',
  jaune: 'bg-amber-500',
  violet: 'bg-violet-500',
  rose: 'bg-rose-500',
};

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export function CoupDePouceGuide() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: 'Aides', url: `${SITE_URL}/particuliers/aides` },
          { name: 'Coup de pouce', url: PAGE_URL },
        ]}
      />
      <ArticleLd
        headline="Coup de pouce CEE 2026 — La prime énergie majorée"
        url={PAGE_URL}
        datePublished="2026-01-01"
        dateModified={new Date().toISOString().slice(0, 10)}
      />
      <FAQPageLd
        items={COUP_FAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <HowToLd
        name="Comment bénéficier d'un Coup de pouce CEE en 2026"
        description="Les 5 étapes pour obtenir une prime énergie bonifiée via le dispositif Coup de pouce."
        steps={COUP_DEMARCHES.map((d) => ({ name: d.title, text: d.description }))}
      />

      <HeroSection />
      <SummarySection />
      <MecanismeSection />
      <OperationsSection />
      <BaremesSection />
      <ConditionsSection />
      <DemarchesSection />
      <CumulSection />
      <FaqSection />
      <FinalCtaSection />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. HERO
// ════════════════════════════════════════════════════════════════

function HeroSection() {
  const coupsActifs = [
    'Chauffage (fossile → décarboné)',
    'Isolation (combles, planchers)',
    'Rénovation performante',
  ];
  return (
    <ParticulierPageHero
      breadcrumb={[
        { name: 'Aides', href: '/particuliers/aides' },
        { name: 'Coup de pouce' },
      ]}
      eyebrow={COUP_HERO.eyebrow}
      headline={COUP_HERO.titlePrimary}
      headlineAccent={COUP_HERO.titleAccent}
      subheadline={COUP_HERO.lead}
      primaryCta={{ label: 'Vérifier mon éligibilité', href: CTA_HREF }}
      secondaryCta={{ label: 'Voir les Coups de pouce actifs', href: '#operations' }}
      microPromise={COUP_HERO.microPromise}
      sideSlot={
        <ParticulierLiveCard eyebrow="Mécanisme">
          <p className="bg-gradient-to-br from-accent-700 via-accent-600 to-brand-600 bg-clip-text text-3xl font-black leading-tight tracking-tight text-transparent">
            {COUP_HERO.amountHeadline}
          </p>
          <p className="mt-3 text-[13px] leading-relaxed text-stone-500">
            {COUP_HERO.amountSubline}
          </p>
          <div className="mt-5 border-t border-stone-100 pt-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
              Coups de pouce actifs
            </p>
            <ul className="mt-3 space-y-1.5 text-[13px] text-stone-700">
              {coupsActifs.map((label) => (
                <li key={label} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 shrink-0 text-brand-500" strokeWidth={2.4} />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </ParticulierLiveCard>
      }
    />
  );
}

// ════════════════════════════════════════════════════════════════
// 2. EN RÉSUMÉ
// ════════════════════════════════════════════════════════════════

function SummarySection() {
  return (
    <section className="border-y border-stone-200/70 bg-stone-50/60 py-10 md:py-14">
      <Container>
        <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
          <div className="mb-5 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              En résumé
            </p>
          </div>
          <ul className="grid gap-3 md:grid-cols-2">
            {COUP_SUMMARY.map((point) => (
              <li
                key={point.text}
                className="flex items-start gap-2.5 rounded-xl border border-stone-100 bg-stone-50/50 p-3.5"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" strokeWidth={2.4} />
                <span className="text-[13.5px] leading-relaxed text-stone-700">{point.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. MÉCANISME
// ════════════════════════════════════════════════════════════════

function MecanismeSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow={COUP_MECANISME.eyebrow}
          title={COUP_MECANISME.title}
          subtitle={COUP_MECANISME.description}
        />
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. LES COUPS DE POUCE EN VIGUEUR
// ════════════════════════════════════════════════════════════════

function OperationsSection() {
  return (
    <section id="operations" className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Les Coups de pouce 2026"
          title="Trois dispositifs en vigueur"
          subtitle="Les Coups de pouce résidentiels couvrent trois grandes familles d’opérations prioritaires. La liste des opérations éligibles et les barèmes évoluent par arrêté."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {COUP_OPERATIONS.map((op) => {
            const Icon = getIcon(op.iconName);
            return (
              <article
                key={op.title}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle"
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent-50 text-accent-800 ring-1 ring-inset ring-accent-200/60">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="text-right text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                    {op.caption}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-ink">{op.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-stone-600">
                    {op.description}
                  </p>
                </div>
                <ul className="mt-1 space-y-1.5 border-t border-stone-100 pt-3">
                  {op.operations.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[12.5px] leading-relaxed text-stone-700"
                    >
                      <CheckCircle2
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-500"
                        strokeWidth={2.2}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {op.note ? (
                  <p className="mt-auto rounded-lg bg-amber-50/70 p-2.5 text-[11.5px] leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200/70">
                    {op.note}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 5. BARÈMES selon profils Anah
// ════════════════════════════════════════════════════════════════

function BaremesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Barèmes selon profils Anah"
          title="La bonification varie selon vos revenus"
          subtitle="Comme MaPrimeRénov’, le Coup de pouce module ses barèmes selon les 4 catégories de revenus de l’Anah. Les ménages aux revenus modestes bénéficient des bonifications les plus généreuses."
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {COUP_BAREMES.map((b) => (
            <article
              key={b.profil}
              className={cn(
                'rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle',
                'transition-shadow duration-200 hover:shadow-medium',
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn('h-2.5 w-2.5 rounded-full', INCOME_DOT_STYLES[b.color])}
                />
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
                    INCOME_COLOR_STYLES[b.color],
                  )}
                >
                  {b.profil}
                </span>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-stone-600">{b.description}</p>
            </article>
          ))}
        </div>

        <p className="mt-6 flex items-start gap-2.5 rounded-xl bg-stone-100/60 p-4 text-[12.5px] leading-relaxed text-stone-600 ring-1 ring-inset ring-stone-200">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" strokeWidth={1.8} />
          Les barèmes précis dépendent de chaque arrêté Coup de pouce, du tarif du marché des
          primes CEE au jour du dépôt et de votre situation. Lancez le calculateur pour obtenir
          une estimation chiffrée actualisée.
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 6. CONDITIONS
// ════════════════════════════════════════════════════════════════

function ConditionsSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Conditions"
          title="4 conditions à respecter pour bénéficier d’un Coup de pouce"
          subtitle="Mêmes règles que pour la prime énergie classique, plus une obligation supplémentaire : passer par un signataire de la charte qualité officielle."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {COUP_CONDITIONS.map((c) => {
            const Icon = getIcon(c.iconName);
            return (
              <article
                key={c.title}
                className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <p className="mt-3 font-display text-base font-bold text-ink">{c.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{c.description}</p>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 7. DÉMARCHES
// ════════════════════════════════════════════════════════════════

function DemarchesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Démarches"
          title="5 étapes pour obtenir votre Coup de pouce"
          subtitle="Les étapes sont identiques à celles de la prime énergie classique, à une différence près : il faut signer l’acte d’engagement avec un signataire de la charte Coup de pouce officielle."
        />

        <ol className="grid gap-4 md:gap-3">
          {COUP_DEMARCHES.map((step, idx) => {
            const Icon = getIcon(step.iconName);
            const isLast = idx === COUP_DEMARCHES.length - 1;
            return (
              <li
                key={step.step}
                className="relative flex items-start gap-5 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle md:p-6"
              >
                <div className="flex shrink-0 flex-col items-center gap-2">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-accent-600 text-base font-bold text-white shadow-glow">
                    {step.step}
                  </span>
                  {!isLast ? (
                    <span aria-hidden className="hidden h-full w-px flex-1 bg-stone-200 md:block" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                      <Icon className="h-4 w-4" strokeWidth={1.7} />
                    </span>
                    <h3 className="font-display text-base font-bold text-ink md:text-lg">{step.title}</h3>
                  </div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">{step.description}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 8. CUMUL
// ════════════════════════════════════════════════════════════════

function CumulSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Cumul avec les autres aides"
          title="Le Coup de pouce s’ajoute aux autres aides"
          subtitle="Le Coup de pouce remplace la prime CEE classique sur l’opération concernée, et se cumule avec MaPrimeRénov’, l’éco-PTZ et la TVA 5,5 %."
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {COUP_CUMULS.map((c) => {
            const Icon = getIcon(c.iconName);
            const Tag = c.href ? 'a' : 'div';
            return (
              <Tag
                key={c.aide}
                {...(c.href ? { href: c.href } : {})}
                className={cn(
                  'group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle',
                  c.href && 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      CUMUL_TONE_STYLES[c.tone],
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  {c.href ? (
                    <ArrowRight
                      className="h-3.5 w-3.5 text-stone-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600"
                      strokeWidth={2.4}
                    />
                  ) : null}
                </div>
                <p className="text-[14px] font-semibold text-ink">{c.aide}</p>
                <p className="text-[12.5px] leading-relaxed text-stone-600">{c.description}</p>
              </Tag>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 9. FAQ
// ════════════════════════════════════════════════════════════════

function FaqSection() {
  return (
    <section className="py-14 md:py-20">
      <Container size="narrow">
        <SectionHeader
          eyebrow="FAQ"
          title="Vos questions sur le Coup de pouce"
          subtitle="Les questions essentielles posées par les propriétaires en 2026."
          align="center"
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <Accordion type="single" collapsible className="divide-y divide-stone-100">
            {COUP_FAQ.map((item, idx) => (
              <AccordionItem
                key={item.question}
                value={`coup-faq-${idx}`}
                className="border-0 px-5"
              >
                <AccordionTrigger className="text-[15px]">{item.question}</AccordionTrigger>
                <AccordionContent className="text-[14px] leading-relaxed text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 10. CTA FINAL
// ════════════════════════════════════════════════════════════════

function FinalCtaSection() {
  return (
    <ParticulierCTAFinal
      badge="Estimation actualisée"
      title="Votre projet est-il éligible à un Coup de pouce ?"
      description={
        <>
          Décrivez votre projet : type de travaux, équipement remplacé, zone climatique
          et revenus du foyer. Notre calculateur croise les bonifications CEE en vigueur,
          MaPrimeRénov&rsquo; et les autres aides cumulables.
        </>
      }
      primaryCta={{ label: 'Lancer la simulation', href: CTA_HREF }}
      secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
    />
  );
}
